import { ClockCircleFilled, UserOutlined } from "@ant-design/icons"
import { Divider } from "components/Divider/Divider"
import { AddressLink } from "components/AddressLink/AddressLink"
import { BackButton } from "components/BackButton/BackButton"
import { Spinner } from "components/Spinner/Spinner"
import { Stepper, StepperStep } from "components/Stepper/Stepper"
import { Step, Visit } from "interface/visit"
import { Chat } from "modules/communications/Chat"
import { Voice } from "modules/communications/Voice"
import { Configuration } from "modules/footprint/Configuration"
import { MarkAsComplete } from "modules/visits/MarkAsComplete"
import { StepLinkFootprint } from "modules/visits/StepLinkFootprint"
import { StepSendLoginLink } from "modules/visits/StepSendLoginLink"
import { StepTestScan } from "modules/visits/StepTestScan"
import { StepUnlinkFootprint } from "modules/visits/StepUnlinkFootprint"
import { StepUpdateFootprint } from "modules/visits/StepUpdateFootprint"
import { StepUploadImage } from "modules/visits/StepUploadImage"
import { useMemo, useState } from "react"
import { useNavigate, useParams } from "react-router"
import { useUserRole } from "services/users"
import { useVisit } from "services/visits"
import { prettyVisitInterval } from "utils/visits"

export function StartVisit() {
	const { scopeId, visitId } = useParams()
	const navigate = useNavigate()
	const { data: visit, isFetching, isFetched } = useVisit(visitId!)
	const { data: { id: userId } = {} } = useUserRole()
	const isAssignedAgent =
		visit && (visit.fieldSupportAgents ?? []).some(({ id }) => id === userId)
	const isDone = ["CANCELLED", "COMPLETED"].includes(visit?.status!)
	const handleNavigateToVisits = () => navigate(`/${scopeId}/visits`)
	const [stepIndex, setStepIndex] = useState(0)
	const steps = useMemo(() => (visit ? buildSteps(visit) : []), [visit])
	const stepKeys = Object.keys(steps) as Step[]
	const stepValues = Object.values(steps) as StepperStep[]
	const isLastStep = stepIndex === stepValues?.length - 1

	if (isFetched && (!visit || !isAssignedAgent || isDone)) {
		handleNavigateToVisits()
	}

	if (!visit) {
		return null
	}

	return (
		<Spinner spinning={isFetching}>
			<div className="@container mx-auto flex h-full max-h-full max-w-300 flex-col">
				<BackButton onClick={handleNavigateToVisits}>My visits</BackButton>

				<div className="mt-6 flex w-full flex-wrap justify-between gap-3">
					<h2 className="font-nunito text-3xl font-semibold">Start visit</h2>

					<div className="flex w-fit gap-2">
						<Voice.CallToAction type="default" patient={visit.patient!} />

						<Chat.CallToAction
							type="default"
							view
							send
							patient={visit.patient!}
							unreadMessagesCount={visit.unreadMessagesCount}
						/>

						{visit.visitType !== "Footprint Pickup" && <Configuration />}
					</div>
				</div>

				<div className="mt-3 flex flex-col gap-2 @5xl:flex-row @5xl:gap-6">
					<p className="flex items-center gap-2">
						<UserOutlined aria-hidden />
						{visit.patient?.fullName}
					</p>

					<p className="flex items-center gap-2">
						<ClockCircleFilled aria-hidden />
						{prettyVisitInterval(visit)}
					</p>

					<AddressLink>{visit.patient?.address}</AddressLink>
				</div>

				{!!visit.notes && (
					<div className="white mt-3 rounded-lg border border-blue-20 bg-blue-0 px-3 py-2 whitespace-pre-wrap">
						{visit.notes}
					</div>
				)}

				<Stepper
					className="mt-4 @5xl:mt-8"
					steps={stepValues}
					onClickStep={setStepIndex}
				/>

				{isLastStep && (
					<>
						<Divider dashed className="hidden @5xl:block" />
						<MarkAsComplete
							visit={visit}
							steps={stepKeys}
							onSuccess={handleNavigateToVisits}
						/>
					</>
				)}
			</div>
		</Spinner>
	)
}

function buildSteps(visit: Visit): Partial<Record<Step, StepperStep>> {
	const linkFootprint: StepperStep = {
		subtitle: "Link the patient's account to their Footprint",
		content: <StepLinkFootprint visit={visit} />,
	}
	const softwareUpdate: StepperStep = {
		subtitle: "Update the Footprint to the latest software",
		content: <StepUpdateFootprint visit={visit} />,
	}
	const testScan: StepperStep = {
		subtitle: "Complete a test scan",
		content: <StepTestScan visit={visit} />,
	}
	const imageUpload: StepperStep = {
		subtitle: "Upload image documenting visit",
		content: <StepUploadImage visit={visit} />,
	}

	if (visit.visitType === "Footprint Installation") {
		const sendLink: StepperStep = {
			subtitle: "Confirm the patient is able to log in",
			content: <StepSendLoginLink visit={visit} />,
		}

		return { linkFootprint, softwareUpdate, testScan, sendLink, imageUpload }
	} else if (visit.visitType === "Footprint Replacement") {
		return { linkFootprint, softwareUpdate, testScan, imageUpload }
	} else if (visit.visitType === "Footprint Pickup") {
		const unlinkFootprint: StepperStep = {
			subtitle: "Unlink Footprint",
			content: <StepUnlinkFootprint visit={visit} />,
		}

		return { unlinkFootprint, imageUpload }
	}

	return {}
}
